import { useState, useEffect, useMemo, useCallback } from "react";
import { usePreventMobileZoom } from "@/hooks/usePreventMobileZoom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import {
  CheckCircle2, Clock, ChefHat, Package, Truck, MapPin, Star, X,
  ArrowLeft, Loader2, MessageCircle, ShoppingBag, Sparkles, Phone, Copy, HelpCircle
} from "lucide-react";
import { toast } from "sonner";
import { TrackingPromoSlider } from "@/components/menu/TrackingPromoSlider";
import { TipSelector } from "@/components/menu/TipSelector";
import { OrderRatingSheet } from "@/components/menu/OrderRatingSheet";
import { CustomerChatSheet } from "@/components/menu/CustomerChatSheet";

type OrderStatus = "received" | "preparing" | "ready" | "out_for_delivery" | "completed" | "cancelled";

interface OrderData {
  id: string;
  status: OrderStatus;
  order_type: string;
  customer_name: string | null;
  customer_phone: string | null;
  customer_id: string | null;
  delivery_address: string | null;
  total: number;
  subtotal: number;
  delivery_fee: number;
  discount: number;
  created_at: string;
  updated_at: string;
  restaurant_id: string;
  notes: string | null;
  payment_method: string | null;
}

interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
}

interface RestaurantInfo {
  name: string;
  logo_url: string | null;
  phone: string | null;
  primary_color: string | null;
  slug: string | null;
  tip_enabled: boolean | null;
  tip_values: number[] | null;
}

interface TrackingSlide {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  link_url: string | null;
}

const fmt = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const STATUS_STEPS: { key: OrderStatus; label: string; icon: React.ElementType; description: string }[] = [
  { key: "received", label: "Pedido recebido", icon: Clock, description: "Seu pedido foi recebido pelo restaurante" },
  { key: "preparing", label: "Em preparo", icon: ChefHat, description: "A cozinha está preparando seu pedido" },
  { key: "ready", label: "Pronto", icon: Package, description: "Seu pedido está pronto!" },
  { key: "out_for_delivery", label: "Saiu para entrega", icon: Truck, description: "O entregador está a caminho" },
  { key: "completed", label: "Entregue", icon: CheckCircle2, description: "Pedido entregue com sucesso!" },
];

// Color segments for the iFood-style multicolor progress bar
const PROGRESS_COLORS = ["#f97316", "#f59e0b", "#22c55e", "#3b82f6", "#8b5cf6"];

const ACTIVE_ORDER_KEY = "viamenu_active_order";

const AcompanharPedido = () => {
  usePreventMobileZoom();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("pedido");
  const restaurantSlug = searchParams.get("r");

  const [order, setOrder] = useState<OrderData | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [restaurant, setRestaurant] = useState<RestaurantInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showRating, setShowRating] = useState(false);
  const [ratingDone, setRatingDone] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [now, setNow] = useState(Date.now());
  const [trackingSlides, setTrackingSlides] = useState<TrackingSlide[]>([]);
  const [tipSent, setTipSent] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const primaryColor = restaurant?.primary_color || "#f97316";
  const ESTIMATED_TOTAL_MIN = 45;

  // Delivery code (last 4 of order id)
  const deliveryCode = order ? order.id.slice(-4).toUpperCase() : "";
  // Order number — same format used in the restaurant panel
  const orderNumber = order ? order.id.slice(0, 6).toUpperCase() : "";

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setCurrentUser(user));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setCurrentUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!orderId) { setError("Pedido não encontrado"); setLoading(false); return; }
    const loadOrder = async () => {
      try {
        const { data, error: fnErr } = await supabase.functions.invoke("get-order-tracking", {
          body: { order_id: orderId },
        });
        if (fnErr || !data?.order) { setError("Pedido não encontrado"); setLoading(false); return; }
        setOrder(data.order as OrderData);
        setOrderItems((data.items || []) as OrderItem[]);
        const restInfo = data.restaurant as RestaurantInfo | null;
        setRestaurant(restInfo);

        // Load tracking slides
        if (data.order?.restaurant_id) {
          const { data: slides } = await supabase
            .from("tracking_slides" as any)
            .select("*")
            .eq("restaurant_id", data.order.restaurant_id)
            .eq("is_active", true)
            .order("sort_order") as any;
          setTrackingSlides((slides || []) as unknown as TrackingSlide[]);
        }
      } catch {
        setError("Erro ao carregar pedido");
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    const statusLabels: Record<string, string> = {
      preparing: "🍳 Seu pedido está sendo preparado!",
      ready: "✅ Seu pedido está pronto!",
      out_for_delivery: "🛵 Seu pedido saiu para entrega!",
      completed: "🎉 Pedido entregue!",
      cancelled: "❌ Pedido cancelado",
    };
    const handleStatusUpdate = (newOrder: OrderData) => {
      setOrder(newOrder);
      if (statusLabels[newOrder.status]) toast(statusLabels[newOrder.status], { duration: 5000 });
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(restaurant?.name || "Seu Pedido", {
          body: statusLabels[newOrder.status] || `Status: ${newOrder.status}`,
          icon: restaurant?.logo_url || "/favicon.ico",
        });
      }
      // Clear active order from localStorage when final
      if (newOrder.status === "completed" || newOrder.status === "cancelled") {
        try { localStorage.removeItem(ACTIVE_ORDER_KEY); } catch {}
      }
      if (newOrder.status === "completed" && !ratingDone) setTimeout(() => setShowRating(true), 1500);
    };

    const channel = supabase
      .channel(`order-tracking-${orderId}`)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "orders", filter: `id=eq.${orderId}` },
        (payload) => handleStatusUpdate(payload.new as OrderData)
      ).subscribe();

    const pollInterval = setInterval(async () => {
      try {
        const { data } = await supabase.functions.invoke("get-order-tracking", { body: { order_id: orderId } });
        if (data?.order) {
          const newOrder = data.order as OrderData;
          setOrder((prev) => {
            if (!prev || prev.status === newOrder.status) return prev || newOrder;
            handleStatusUpdate(newOrder);
            return newOrder;
          });
        }
      } catch {}
    }, 10000);

    return () => { supabase.removeChannel(channel); clearInterval(pollInterval); };
  }, [orderId, restaurant, ratingDone]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  }, []);

  const currentStepIndex = useMemo(() => {
    if (!order) return 0;
    return STATUS_STEPS.findIndex((s) => s.key === order.status);
  }, [order]);

  const elapsedMinutes = useMemo(() => {
    if (!order) return 0;
    return Math.floor((now - new Date(order.created_at).getTime()) / 60000);
  }, [order, now]);

  const countdown = useMemo(() => {
    if (!order || order.status === "completed" || order.status === "cancelled") return null;
    const orderTime = new Date(order.created_at).getTime();
    const etaMs = orderTime + ESTIMATED_TOTAL_MIN * 60000;
    const remainingMs = Math.max(0, etaMs - now);
    const remainingMin = Math.floor(remainingMs / 60000);
    const progress = Math.min(1, (now - orderTime) / (ESTIMATED_TOTAL_MIN * 60000));
    return { remainingMin, progress, isOverdue: remainingMs === 0 };
  }, [order, now]);

  const etaRange = useMemo(() => {
    if (!order) return "";
    const base = new Date(order.created_at);
    const min = new Date(base.getTime() + (ESTIMATED_TOTAL_MIN - 10) * 60000);
    const max = new Date(base.getTime() + ESTIMATED_TOTAL_MIN * 60000);
    const f = (d: Date) => d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return `${f(min)} - ${f(max)}`;
  }, [order]);

  const handleSubmitRating = async (data: {
    foodRating: number; serviceRating: number; deliveryRating: number;
    tags: string[]; comment: string; npsScore: number | null;
  }) => {
    if (!orderId || !order) return;
    await supabase.functions.invoke("submit-rating", {
      body: {
        order_id: orderId,
        food_rating: data.foodRating,
        service_rating: data.serviceRating,
        delivery_rating: data.deliveryRating,
        comment: data.comment || null,
        tags: data.tags,
        nps_score: data.npsScore,
        customer_name: order.customer_name || null,
      },
    });
    setRatingDone(true);
    setShowRating(false);
    toast.success("Obrigado pela avaliação! 🎉");
  };

  const handleTip = async (amount: number) => {
    if (!order || !restaurant) return;
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("process-delivery-tip", {
        body: {
          order_id: order.id,
          restaurant_id: order.restaurant_id,
          amount,
          customer_name: order.customer_name,
          customer_phone: order.customer_phone,
        },
      });
      if (fnErr || !data?.success) {
        toast.error("Erro ao processar gorjeta. Tente novamente.");
        return;
      }
      if (data.qr_code) {
        navigator.clipboard.writeText(data.qr_code);
        toast.success(`PIX de ${fmt(amount)} gerado! Código copiado.`, { duration: 8000 });
      } else {
        toast.success(`Gorjeta de ${fmt(amount)} processada! 🎉`);
      }
      setTipSent(true);
    } catch {
      toast.error("Erro ao processar gorjeta.");
    }
  };

  const copyTrackingLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success("Link de rastreio copiado!");
  };

  if (loading) {
    return (
      <div className="light min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: primaryColor }} />
        <p className="text-sm text-[hsl(0,0%,55%)] mt-3">Carregando pedido...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="light min-h-screen flex flex-col items-center justify-center bg-white p-8 text-center">
        <div className="w-20 h-20 rounded-full bg-[hsl(0,80%,95%)] flex items-center justify-center mx-auto mb-5">
          <X className="h-10 w-10 text-[hsl(0,70%,50%)]" />
        </div>
        <h2 className="text-xl font-bold text-[hsl(0,0%,13%)] mb-2">Pedido não encontrado</h2>
        <p className="text-sm text-[hsl(0,0%,55%)] max-w-[280px]">{error || "Não foi possível localizar este pedido."}</p>
        {restaurantSlug && (
          <button onClick={() => navigate(`/${restaurantSlug}`)} className="mt-6 px-8 py-3.5 rounded-2xl font-bold text-sm text-white shadow-lg" style={{ backgroundColor: primaryColor }}>
            Voltar ao cardápio
          </button>
        )}
      </div>
    );
  }

  const isCancelled = order.status === "cancelled";
  const isCompleted = order.status === "completed";
  const isDelivery = order.order_type === "delivery";
  const activeSteps = isDelivery ? STATUS_STEPS : STATUS_STEPS.filter(s => s.key !== "out_for_delivery");

  const paymentLabels: Record<string, string> = {
    pix: "PIX", credit_card: "Cartão de Crédito", debit_card: "Débito",
    cash: "Dinheiro", mercadopago: "Mercado Pago",
  };

  return (
    <div className="light min-h-screen bg-[hsl(0,0%,97%)] max-w-lg mx-auto relative">
      {/* Header — iFood style */}
      <div className="sticky top-0 z-20 bg-white border-b border-[hsl(0,0%,93%)]">
        <div className="flex items-center justify-between px-4 py-3">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => restaurantSlug ? navigate(`/${restaurantSlug}`) : navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ color: primaryColor }}
          >
            <ArrowLeft className="h-5 w-5" />
          </motion.button>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowHelp(!showHelp)}
              className="px-3 py-1.5 rounded-full border border-[hsl(0,0%,88%)] text-xs font-semibold" 
              style={{ color: primaryColor }}
            >
              Ajuda
            </button>
            <button className="w-10 h-10 rounded-full flex items-center justify-center" style={{ color: primaryColor }}>
              <MessageCircle className="h-5 w-5" />
            </button>
        </div>
        {/* Help dropdown */}
        <AnimatePresence>
          {showHelp && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-[hsl(0,0%,93%)] bg-white"
            >
              <div className="px-4 py-3 space-y-1">
                <p className="text-xs text-[hsl(0,0%,55%)] mb-2">Como podemos ajudar?</p>
                {[
                  { label: "Meu pedido está atrasado", msg: `Olá! Meu pedido #${order?.id?.slice(0, 6).toUpperCase() || ""} está atrasado. Poderia verificar?` },
                  { label: "Problemas com o Motoboy", msg: `Olá! Estou com problemas com o motoboy do pedido #${order?.id?.slice(0, 6).toUpperCase() || ""}. Poderia me ajudar?` },
                  { label: "Problemas com meu pedido", msg: `Olá! Tenho um problema com meu pedido #${order?.id?.slice(0, 6).toUpperCase() || ""}. Poderia me ajudar?` },
                ].map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => {
                      const phone = restaurant?.phone?.replace(/\D/g, "") || "";
                      if (phone) {
                        const url = `https://wa.me/55${phone}?text=${encodeURIComponent(opt.msg)}`;
                        window.open(url, "_blank");
                      } else {
                        toast.error("Telefone da loja não disponível");
                      }
                      setShowHelp(false);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium text-[hsl(0,0%,20%)] hover:bg-[hsl(0,0%,96%)] transition-colors flex items-center gap-2"
                  >
                    <Phone className="h-4 w-4 text-[hsl(0,0%,50%)]" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Status Card — Main hero */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h2 className="text-lg font-bold text-[hsl(0,0%,10%)]">
            {isCancelled ? "Pedido Cancelado" : STATUS_STEPS.find(s => s.key === order.status)?.label || "Processando"}
          </h2>
          
          {!isCancelled && !isCompleted && (
            <>
              {/* Multi-color progress bar — iFood style */}
              <div className="mt-3 flex gap-1">
                {activeSteps.map((step, idx) => {
                  const isActive = idx <= currentStepIndex;
                  const isCurrent = idx === currentStepIndex;
                  return (
                    <div key={idx} className="flex-1 h-[5px] rounded-full overflow-hidden bg-[hsl(0,0%,90%)] relative">
                      {isActive && (
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: PROGRESS_COLORS[idx % PROGRESS_COLORS.length] }}
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          transition={{ duration: 0.6, delay: idx * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
                        />
                      )}
                      {isCurrent && isActive && (
                        <motion.div
                          className="h-full rounded-full absolute inset-0"
                          style={{ backgroundColor: PROGRESS_COLORS[idx % PROGRESS_COLORS.length] }}
                          animate={{ opacity: [0.4, 0.8, 0.4] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ETA */}
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-[hsl(0,0%,50%)]">
                    {isDelivery ? "Previsão de entrega:" : "Previsão:"}{" "}
                    <span className="font-bold text-[hsl(0,0%,13%)]">
                      {countdown?.isOverdue ? "Chegando..." : countdown ? `${countdown.remainingMin} minutos` : etaRange}
                    </span>
                  </p>
                </div>
              </div>

              {/* Delivery code */}
              <div className="mt-3 flex items-center justify-between py-2.5 px-3 bg-[hsl(0,0%,97%)] rounded-xl">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-[hsl(0,0%,50%)]">Código de entrega</span>
                  <HelpCircle className="h-3.5 w-3.5 text-[hsl(0,0%,60%)]" />
                </div>
                <div className="flex items-center gap-1.5 bg-[hsl(0,0%,92%)] px-3 py-1.5 rounded-lg">
                  <span className="text-xs text-[hsl(0,0%,55%)]">····</span>
                  <span className="font-bold text-sm text-[hsl(0,0%,13%)] tracking-wider">{deliveryCode}</span>
                </div>
              </div>

              {/* Overdue warning */}
              {countdown?.isOverdue && (
                <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-[hsl(0,90%,97%)] border border-[hsl(0,80%,90%)]">
                  <span className="w-2 h-2 rounded-full bg-[hsl(0,80%,55%)]" />
                  <p className="text-xs text-[hsl(0,0%,40%)]">
                    Sentimos muito pelo atraso, seu pedido vai chegar em breve
                  </p>
                </div>
              )}
            </>
          )}

          {isCancelled && (
            <p className="text-sm text-[hsl(0,0%,55%)] mt-2">Este pedido foi cancelado.</p>
          )}

          {isCompleted && (
            <div className="mt-2 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-[hsl(142,71%,45%)]" />
              <p className="text-sm text-[hsl(142,71%,35%)] font-medium">Pedido concluído com sucesso!</p>
            </div>
          )}
        </div>

        {/* Promo Slider — iFood style */}
        {trackingSlides.length > 0 && !isCompleted && !isCancelled && (
          <TrackingPromoSlider slides={trackingSlides} primaryColor={primaryColor} />
        )}

        {/* Tip selector — for delivery orders when out_for_delivery or completed and tip is enabled */}
        {isDelivery && !tipSent && restaurant?.tip_enabled && (order.status === "out_for_delivery" || isCompleted) && (
          <TipSelector onTipSelect={handleTip} primaryColor={primaryColor} fmt={fmt} tipValues={restaurant?.tip_values || undefined} />
        )}

        {/* Delivery address */}
        {order.delivery_address && (
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <h3 className="font-bold text-sm text-[hsl(0,0%,13%)] mb-3">Endereço de entrega</h3>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[hsl(0,0%,96%)] flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-[hsl(0,0%,40%)]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[hsl(0,0%,13%)]">{order.delivery_address.split(",")[0]}</p>
                <p className="text-xs text-[hsl(0,0%,55%)] mt-0.5">{order.delivery_address.split(",").slice(1).join(",").trim()}</p>
              </div>
            </div>
          </div>
        )}

        {/* Order details — iFood style */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="p-4 flex items-center gap-3">
            {restaurant?.logo_url ? (
              <img src={restaurant.logo_url} alt="" className="w-11 h-11 rounded-xl object-cover" />
            ) : (
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold" style={{ backgroundColor: primaryColor }}>
                {restaurant?.name?.charAt(0) || "R"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-[hsl(0,0%,13%)]">{restaurant?.name}</p>
              <p className="text-xs text-[hsl(0,0%,55%)]">
                Pedido nº {orderNumber} • {orderItems.length} {orderItems.length === 1 ? "item" : "itens"}
              </p>
            </div>
            <button 
              onClick={() => {
                const slug = restaurantSlug || restaurant?.slug;
                if (slug) navigate(`/${slug}`);
              }}
              style={{ color: primaryColor }} 
              className="text-xs font-semibold"
            >
              Ver cardápio
            </button>
          </div>

          {/* Items */}
          <div className="border-t border-[hsl(0,0%,95%)]">
            {orderItems.map((item) => (
              <div key={item.id} className="px-4 py-3 border-b border-[hsl(0,0%,97%)] last:border-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-6 h-6 rounded-md text-[10px] font-bold flex items-center justify-center bg-[hsl(0,0%,95%)] text-[hsl(0,0%,40%)]">
                      {item.quantity}
                    </span>
                    <p className="text-sm text-[hsl(0,0%,20%)]">{item.product_name}</p>
                  </div>
                  <p className="text-sm font-semibold text-[hsl(0,0%,13%)] tabular-nums">{fmt(Number(item.total_price))}</p>
                </div>
                {item.notes && (
                  <div className="ml-[2.125rem] mt-1 space-y-0.5">
                    {item.notes.split(";").map((addon, idx) => (
                      <p key={idx} className="text-xs text-[hsl(0,0%,50%)]">{addon.trim()}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Values summary — iFood style */}
          <div className="px-4 py-4 space-y-2 bg-[hsl(0,0%,99%)]">
            <h4 className="font-bold text-sm text-[hsl(0,0%,13%)] mb-2">Resumo de valores</h4>
            <div className="flex justify-between text-sm text-[hsl(0,0%,45%)]">
              <span>Subtotal</span>
              <span className="tabular-nums">{fmt(Number(order.subtotal))}</span>
            </div>
            {Number(order.discount) > 0 && (
              <div className="flex justify-between text-sm font-medium text-[hsl(0,80%,50%)]">
                <span>Cupom</span>
                <span className="tabular-nums">-{fmt(Number(order.discount))}</span>
              </div>
            )}
            {Number(order.delivery_fee) > 0 && (
              <div className="flex justify-between text-sm text-[hsl(0,0%,45%)]">
                <span>Taxa de entrega</span>
                <span className="tabular-nums">{fmt(Number(order.delivery_fee))}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold text-[hsl(0,0%,10%)] pt-2 border-t border-[hsl(0,0%,92%)]">
              <span>Total</span>
              <span className="tabular-nums">{fmt(Number(order.total))}</span>
            </div>

            {/* Payment method */}
            {(order as any).payment_method && (
              <div className="pt-2">
                <p className="font-bold text-sm text-[hsl(0,0%,13%)]">Pago</p>
                <p className="text-xs text-[hsl(0,0%,55%)] mt-0.5">{paymentLabels[(order as any).payment_method] || (order as any).payment_method}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3 pt-2">
          {/* Chat with restaurant */}
          {!isCancelled && (
            <motion.button
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowChat(true)}
              className="w-full py-3.5 rounded-2xl font-bold text-[14px] border-2 transition-colors flex items-center justify-center gap-2"
              style={{ borderColor: primaryColor, color: primaryColor }}
            >
              <MessageCircle className="h-5 w-5" /> Chat com a loja
            </motion.button>
          )}

          {/* Rating */}
          {isCompleted && !ratingDone && (
            <motion.button
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowRating(true)}
              className="w-full py-3.5 rounded-2xl font-bold text-[14px] text-white shadow-lg transition-transform flex items-center justify-center gap-2"
              style={{ backgroundColor: primaryColor, boxShadow: `0 8px 24px ${primaryColor}30` }}
            >
              <Sparkles className="h-4.5 w-4.5" /> Avaliar pedido
            </motion.button>
          )}

          {(isCompleted || isCancelled) && (
            <button
              onClick={() => {
                const slug = restaurantSlug || (restaurant as any)?.slug;
                slug ? navigate(`/${slug}`) : navigate(-1);
              }}
              className="w-full py-3.5 rounded-2xl border-2 border-[hsl(0,0%,88%)] text-[hsl(0,0%,30%)] font-semibold text-[14px] hover:bg-[hsl(0,0%,97%)] transition-colors"
            >
              Voltar ao cardápio
            </button>
          )}
        </div>
      </div>

      {/* Rating Bottom Sheet — iFood style */}
      <AnimatePresence>
        {showRating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
            onClick={() => { setShowRating(false); setRatingDone(true); }}
          >
            <OrderRatingSheet
              restaurantName={restaurant?.name || "Restaurante"}
              restaurantLogo={restaurant?.logo_url}
              orderDate={new Date(order.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
              orderItems={orderItems.map(i => ({ name: i.product_name, quantity: i.quantity }))}
              primaryColor={primaryColor}
              onSubmit={handleSubmitRating}
              onSkip={() => { setShowRating(false); setRatingDone(true); }}
              showDeliveryRating={isDelivery}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Customer Chat Sheet */}
      <CustomerChatSheet
        open={showChat}
        onClose={() => setShowChat(false)}
        orderId={order.id}
        restaurantId={order.restaurant_id}
        restaurantName={restaurant?.name || "Restaurante"}
        restaurantLogo={restaurant?.logo_url || null}
        customerName={order.customer_name}
        customerPhone={order.customer_phone}
        customerId={order.customer_id}
        primaryColor={primaryColor}
        orderStatus={order.status}
        orderTotal={Number(order.total)}
        fmt={fmt}
      />
    </div>
  );
};

export default AcompanharPedido;
